import { Pencil, Trash2, CreditCard, Banknote, Smartphone, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import type { PaymentMethod, PaymentMethodType } from "@/hooks/usePaymentMethods";

interface PaymentMethodListProps {
  paymentMethods: PaymentMethod[];
  isLoading: boolean;
  onEdit: (method: PaymentMethod) => void;
  onDelete: (id: string) => void;
  onToggleActive: (id: string) => void;
}

const typeIcons: Record<PaymentMethodType, React.ReactNode> = {
  cash: <Banknote className="h-4 w-4 text-green-600" />,
  card: <CreditCard className="h-4 w-4 text-blue-600" />,
  digital_wallet: <Smartphone className="h-4 w-4 text-purple-600" />,
};

const typeLabels: Record<PaymentMethodType, string> = {
  cash: "Dinheiro",
  card: "Cartão",
  digital_wallet: "Carteira Digital",
};

export function PaymentMethodList({
  paymentMethods,
  isLoading,
  onEdit,
  onDelete,
  onToggleActive,
}: PaymentMethodListProps) {
  if (isLoading) {
    return (
      <div className="rounded-lg border bg-card">
        <div className="p-4 space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-center gap-4">
              <Skeleton className="h-10 w-10 rounded-full" />
              <Skeleton className="h-4 flex-1" />
              <Skeleton className="h-8 w-20" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (paymentMethods.length === 0) {
    return (
      <div className="rounded-lg border bg-card p-8 text-center">
        <CreditCard className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium">Nenhuma forma de pagamento</h3>
        <p className="text-muted-foreground">
          Adicione formas de pagamento para usar no PDV
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nome</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead className="text-center">Taxa</TableHead>
            <TableHead className="text-center">Recebimento</TableHead>
            <TableHead className="text-center">Ativo</TableHead>
            <TableHead className="text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {paymentMethods.map((method) => (
            <TableRow key={method.id}>
              <TableCell>
                <div className="flex items-center gap-2">
                  {typeIcons[method.type]}
                  <span className="font-medium">{method.name}</span>
                  {method.is_system_default && (
                    <Badge variant="secondary" className="text-xs gap-1">
                      <Lock className="h-3 w-3" />
                      Padrão
                    </Badge>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <Badge variant="outline">{typeLabels[method.type]}</Badge>
              </TableCell>
              <TableCell className="text-center">
                <span>{method.fee_percentage}%</span>
              </TableCell>
              <TableCell className="text-center">
                <span>
                  {method.receiving_days === 0
                    ? "Mesmo dia"
                    : `${method.receiving_days} dias`}
                </span>
              </TableCell>
              <TableCell className="text-center">
                <Switch
                  checked={method.is_active}
                  onCheckedChange={() => onToggleActive(method.id)}
                />
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onEdit(method)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  {!method.is_system_default && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Excluir forma de pagamento?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Esta ação não pode ser desfeita. A forma de pagamento "{method.name}" será removida permanentemente.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => onDelete(method.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Excluir
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
